import React, { useMemo } from "react";
import { Badge, Group, Progress, Skeleton, Stack, Text, UnstyledButton } from "@mantine/core";
import { SectionCard, spacePx } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { BreakdownRow, MonitoringBreakdown } from "@models/adminMonitoring";
import { formatCount, formatMoney, moduleLabel } from "@utils/monitoringFormat";

interface BreakdownCardProps {
  title: string;
  data: MonitoringBreakdown | undefined | null;
  loading?: boolean;
  limit?: number;
  onSelect?: (row: BreakdownRow) => void;
}

/**
 * The top values of one dimension with their share of the currency's priced
 * cost. Rows of every currency are listed (never added together); a value with
 * no priced cost still shows its event count.
 */
const BreakdownCard: React.FC<BreakdownCardProps> = ({ title, data, loading, limit = 6, onSelect }) => {
  const { t } = useTranslation();
  const rows = useMemo(
    () => [...(data?.rows ?? [])].sort((a, b) => b.share - a.share || b.event_count - a.event_count).slice(0, limit),
    [data, limit],
  );
  const hidden = (data?.rows.length ?? 0) - rows.length;
  const name = (row: BreakdownRow) =>
    row.label ?? (data?.dimension === "module" ? moduleLabel(row.value) : row.value);

  return (
    <SectionCard title={title}>
      {loading && !data ? (
        <Skeleton h={180} />
      ) : !rows.length ? (
        <Text size="sm" c="dimmed">{t("admin.insights.empty.title")}</Text>
      ) : (
        <Stack gap="sm">
          {rows.map((row) => {
            const content = (
              <Stack gap={spacePx[1]}>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Text size="sm" fw={500} truncate title={row.value}>{name(row)}</Text>
                  <Group gap="xs" wrap="nowrap">
                    {row.unpriced_count > 0 && (
                      <Badge size="xs" color="orange" variant="light">
                        {t("admin.insights.unpricedBadge", { count: row.unpriced_count })}
                      </Badge>
                    )}
                    <Text size="sm" fw={600}>{formatMoney(row.total_cost, row.currency)}</Text>
                  </Group>
                </Group>
                <Progress value={row.share} size="sm" aria-label={`${row.share}%`} />
                <Text size="xs" c="dimmed">
                  {t("admin.insights.breakdownMeta", { events: formatCount(row.event_count), share: row.share })}
                </Text>
              </Stack>
            );
            return onSelect ? (
              <UnstyledButton key={`${row.value}-${row.currency}`} onClick={() => onSelect(row)}>
                {content}
              </UnstyledButton>
            ) : (
              <div key={`${row.value}-${row.currency}`}>{content}</div>
            );
          })}
          {hidden > 0 && <Text size="xs" c="dimmed">{t("admin.insights.moreRows", { count: hidden })}</Text>}
        </Stack>
      )}
    </SectionCard>
  );
};

export default BreakdownCard;
